import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Activity,
  Bot,
  ChevronRight,
  AlertTriangle,
  ListTodo,
  MessageSquare,
  RefreshCw,
  ShieldCheck,
  Signal,
  Sparkles,
  Timer,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  fetchThreads,
  fetchBlackboardRecent,
  fetchThreadNotes,
  fetchGlobalTimeline,
  type ObservabilityThread,
  type BlackboardNote,
  type TimelineEvent,
} from "@/api/observability";
import { request } from "@/api/client";
import { resolveApiUrl } from "@/lib/api";
import { fetchHitlInbox } from "@/api/hitl";
import type { HitlItem } from "@/types/hitl";
import AgentInboxPanel from "@/components/agents/AgentInboxPanel";
import CapabilityGate from "@/components/CapabilityGate";
import LockedFeatureCard from "@/components/LockedFeatureCard";
import HitlDetailDrawer from "@/components/hitl/HitlDetailDrawer";
import HitlBadge from "@/components/hitl/HitlBadge";
import HitlAssignMenu from "@/components/hitl/HitlAssignMenu";
import LockedHitlDrawer from "@/components/hitl/LockedHitlDrawer";
import { useHitlActions } from "@/hooks/useHitlActions";
import { useHasCapability } from "@/hooks/useCapabilities";
import { useUpgradeModal } from "@/hooks/useUpgradeModal";
import {
  type ThreadDrawerState,
  type ArtistSignal,
  type ConversationItem,
  type UpcomingEvent,
  type ActionQueueItem,
  type TimeRangeKey,
  type EventFilter,
  TIME_RANGES,
  formatDate,
  formatRelativeTime,
  summarizeNote,
  getEventBucket,
  fetchArtistSignalsData,
  buildConversationItems,
  getMomentumTone,
  formatNumber,
  formatShortDate,
  normalizeCalendarItem,
  minutesSince,
  severityTone,
} from "./observabilityUtils";

export default function AgentsObservabilityDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedHitlId = searchParams.get("open");
  const threadParam = searchParams.get("thread_id");
  const [threads, setThreads] = useState<ObservabilityThread[]>([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [blackboardNotes, setBlackboardNotes] = useState<BlackboardNote[]>([]);
  const [blackboardLoading, setBlackboardLoading] = useState(false);
  const [hitlQueue, setHitlQueue] = useState<HitlItem[]>([]);
  const [hitlLoading, setHitlLoading] = useState(false);
  const [activityEvents, setActivityEvents] = useState<TimelineEvent[]>([]);
  const [timelineVisible, setTimelineVisible] = useState(25);
  const [hitlVisible, setHitlVisible] = useState(10);
  const [agentFilter, setAgentFilter] = useState<string>("all");
  const [activeOnly, setActiveOnly] = useState(true);
  const [drawer, setDrawer] = useState<ThreadDrawerState | null>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerError, setDrawerError] = useState<string | null>(null);
  const [autoRefresh] = useState(false);
  const [sseConnected, setSseConnected] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [timeRange, setTimeRange] = useState<TimeRangeKey>("1h");
  const [eventFilter, setEventFilter] = useState<EventFilter>("all");
  const [pendingThreshold, setPendingThreshold] = useState(10);
  const [staleApprovalMinutes, setStaleApprovalMinutes] = useState(90);
  const [quietMinutesThreshold, setQuietMinutesThreshold] = useState(30);
  const [upcomingEvents, setUpcomingEvents] = useState<UpcomingEvent[]>([]);
  const [upcomingLoading, setUpcomingLoading] = useState(false);
  const [artistSignalsList, setArtistSignalsList] = useState<ArtistSignal[]>([]);
  const hitlActions = useHitlActions();
  const { openUpgradeModal } = useUpgradeModal();
  const canViewHitl = useHasCapability("canViewHitl");
  const canOpenHitlDrawer = useHasCapability("canOpenHitlDrawer");

  const metrics = useMemo(() => {
    return {
      hitlPending: hitlQueue.length,
      recentNotes: blackboardNotes.length,
    };
  }, [hitlQueue, blackboardNotes]);

  const refreshThreads = useCallback(() => {
    setThreadsLoading(true);
    fetchThreads()
      .then(setThreads)
      .finally(() => setThreadsLoading(false));
  }, []);

  const refreshBlackboard = useCallback(() => {
    setBlackboardLoading(true);
    fetchBlackboardRecent(50)
      .then(setBlackboardNotes)
      .finally(() => setBlackboardLoading(false));
  }, []);

  const refreshHitl = useCallback(() => {
    setHitlLoading(true);
    fetchHitlInbox({ status: "pending", category: "all", limit: 50 })
      .then(setHitlQueue)
      .finally(() => setHitlLoading(false));
  }, []);

  const refreshActivity = useCallback(() => {
    fetchGlobalTimeline(200).then(setActivityEvents);
  }, []);

  const refreshUpcoming = useCallback(() => {
    setUpcomingLoading(true);
    Promise.allSettled([
      request("/booking/shows/calendar"),
      request("/touring/stops/calendar"),
    ])
      .then((results) => {
        const events: UpcomingEvent[] = [];
        const sources = ["booking", "touring"];
        results.forEach((result, index) => {
          if (result.status !== "fulfilled" || !Array.isArray(result.value)) return;
          const source = sources[index];
          result.value.forEach((item) => {
            const event = normalizeCalendarItem(source, item);
            if (event) events.push(event);
          });
        });
        const upcoming = events
          .filter((event) => !Number.isNaN(Date.parse(event.date)))
          .sort((a, b) => Date.parse(a.date) - Date.parse(b.date))
          .slice(0, 8);
        setUpcomingEvents(upcoming);
      })
      .finally(() => setUpcomingLoading(false));
  }, []);

  const refreshArtistSignals = useCallback(() => {
    fetchArtistSignalsData().then(setArtistSignalsList).catch(() => {});
  }, []);

  const refreshAll = useCallback(() => {
    refreshThreads();
    refreshBlackboard();
    refreshHitl();
    refreshActivity();
    refreshUpcoming();
    refreshArtistSignals();
    setLastRefresh(new Date());
  }, [refreshThreads, refreshBlackboard, refreshHitl, refreshActivity, refreshUpcoming, refreshArtistSignals]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  // SSE: live refresh whenever any agent event fires — replaces dumb polling
  const refreshAllRef = useRef(refreshAll);
  refreshAllRef.current = refreshAll;
  useEffect(() => {
    let debounce: number | null = null;
    let es: EventSource;
    try {
      es = new EventSource(resolveApiUrl("/stream"));
    } catch {
      return;
    }
    es.onopen = () => setSseConnected(true);
    es.onerror = () => setSseConnected(false);
    const handler = () => {
      if (debounce !== null) window.clearTimeout(debounce);
      debounce = window.setTimeout(() => { void refreshAllRef.current(); debounce = null; }, 1500);
    };
    es.addEventListener("thread_update", handler);
    // 30s heartbeat fallback in case SSE misses events
    const fallback = setInterval(() => { if (autoRefresh) void refreshAllRef.current(); }, 30_000);
    return () => {
      es.removeEventListener("thread_update", handler);
      es.close();
      setSseConnected(false);
      clearInterval(fallback);
      if (debounce !== null) window.clearTimeout(debounce);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const agentsList = useMemo(() => {
    const names = Array.from(new Set(threads.map((thread) => thread.agent))).sort();
    return names;
  }, [threads]);

  const filteredThreads = useMemo(() => {
    return threads.filter((thread) => {
      if (agentFilter !== "all" && thread.agent !== agentFilter) {
        return false;
      }
      if (activeOnly && !thread.active) {
        return false;
      }
      return true;
    });
  }, [threads, agentFilter, activeOnly]);

  const filteredActivityEvents = useMemo(() => {
    const windowMs = TIME_RANGES[timeRange].minutes * 60 * 1000;
    const now = Date.now();
    return activityEvents.filter((event) => {
      if (!event.timestamp) return false;
      const ts = Date.parse(event.timestamp);
      if (Number.isNaN(ts)) return false;
      if (now - ts > windowMs) return false;
      if (eventFilter === "all") return true;
      return getEventBucket(event) === eventFilter;
    });
  }, [activityEvents, eventFilter, timeRange]);

  const hasRecentActivity = useMemo(() => {
    if (quietMinutesThreshold <= 0) return true;
    const windowMs = quietMinutesThreshold * 60 * 1000;
    const now = Date.now();
    return activityEvents.some((event) => {
      if (!event.timestamp) return false;
      const ts = Date.parse(event.timestamp);
      if (Number.isNaN(ts)) return false;
      return now - ts <= windowMs;
    });
  }, [activityEvents, quietMinutesThreshold]);

  const filteredBlackboardNotes = useMemo(() => {
    const windowMs = TIME_RANGES[timeRange].minutes * 60 * 1000;
    const now = Date.now();
    return blackboardNotes.filter((note) => {
      if (!note.created_at) return false;
      const ts = Date.parse(note.created_at);
      if (Number.isNaN(ts)) return false;
      return now - ts <= windowMs;
    });
  }, [blackboardNotes, timeRange]);

  const artistSignals = artistSignalsList;
  const conversationItems = useMemo(() => buildConversationItems(filteredBlackboardNotes), [filteredBlackboardNotes]);

  const activitySummary = useMemo(() => {
    const counts = {
      thoughts: 0,
      actions: 0,
      hitl: 0,
      executed: 0,
      delegations: 0,
      errors: 0,
    };
    filteredActivityEvents.forEach((event) => {
      const type = (event.event_type || "").toLowerCase();
      if (type.includes("thought")) counts.thoughts += 1;
      if (type.startsWith("action")) counts.actions += 1;
      if (type.includes("pending_hitl") || type.includes("hitl")) counts.hitl += 1;
      if (type.includes("observation") || type.includes("executed")) counts.executed += 1;
      if (type.includes("delegation")) counts.delegations += 1;
      if (type.includes("error") || type.includes("failed")) counts.errors += 1;
    });
    return counts;
  }, [filteredActivityEvents]);

  const actionQueue = useMemo<ActionQueueItem[]>(() => {
    const queue: ActionQueueItem[] = [];
    hitlQueue.forEach((item) => {
      const ageMinutes = minutesSince(item.created_at ?? undefined) ?? 0;
      const severity =
        ageMinutes >= staleApprovalMinutes ? "high" : ageMinutes >= Math.max(30, staleApprovalMinutes / 2) ? "medium" : "low";
      queue.push({
        id: item.id,
        label: item.title ?? item.node_key ?? "Pending approval",
        description: item.summary ?? item.description ?? item.agent ?? "Awaiting review",
        ageMinutes,
        severity,
        actionType: "hitl",
        agent: item.agent ?? undefined,
      });
    });

    return queue.sort((a, b) => {
      const severityRank = { high: 3, medium: 2, low: 1 };
      const diff = severityRank[b.severity] - severityRank[a.severity];
      if (diff !== 0) return diff;
      return (b.ageMinutes ?? 0) - (a.ageMinutes ?? 0);
    });
  }, [hitlQueue]);

  const alertItems = useMemo(() => {
    const alerts: { label: string; description: string }[] = [];
    if (metrics.hitlPending >= pendingThreshold) {
      alerts.push({
        label: "Approval queue is growing",
        description: `${metrics.hitlPending} HITL items pending (threshold ${pendingThreshold}).`,
      });
    }
    const staleItems = actionQueue.filter((item) => (item.ageMinutes ?? 0) >= staleApprovalMinutes);
    if (staleItems.length > 0) {
      alerts.push({
        label: "Stale approvals detected",
        description: `${staleItems.length} approvals older than ${staleApprovalMinutes} minutes.`,
      });
    }
    if (!hasRecentActivity && quietMinutesThreshold > 0) {
      alerts.push({
        label: "No recent activity",
        description: `No events in the last ${quietMinutesThreshold} minutes.`,
      });
    }
    return alerts;
  }, [actionQueue, hasRecentActivity, metrics.hitlPending, pendingThreshold, quietMinutesThreshold, staleApprovalMinutes]);

  const openDrawer = (thread: ObservabilityThread) => {
    setDrawer({ thread, notes: [] });
    setDrawerLoading(true);
    setDrawerError(null);
    fetchThreadNotes(thread.agent, thread.id)
      .then((notes) => setDrawer({ thread, notes }))
      .catch((err) => setDrawerError(err instanceof Error ? err.message : "Failed to load thread notes."))
      .finally(() => setDrawerLoading(false));
    applySearch({ threadId: thread.id });
  };

  const closeDrawer = () => {
    setDrawer(null);
    setDrawerLoading(false);
    setDrawerError(null);
    applySearch({ threadId: null });
  };

  const applySearch = useCallback(
    (overrides: { openId?: string | null; threadId?: string | null } = {}) => {
      const params = new URLSearchParams();
      const openValue = overrides.openId === undefined ? selectedHitlId : overrides.openId;
      const threadValue = overrides.threadId === undefined ? threadParam : overrides.threadId;
      if (openValue) params.set("open", openValue);
      if (threadValue) params.set("thread_id", threadValue);
      setSearchParams(params, { replace: true });
    },
    [selectedHitlId, setSearchParams, threadParam],
  );

  useEffect(() => {
    if (!threadParam) return;
    const existing = threads.find((thread) => thread.id === threadParam);
    if (!existing) return;
    setDrawer({ thread: existing, notes: [] });
    setDrawerLoading(true);
    setDrawerError(null);
    fetchThreadNotes(existing.agent, existing.id)
      .then((notes) => setDrawer({ thread: existing, notes }))
      .catch((err) => setDrawerError(err instanceof Error ? err.message : "Failed to load thread notes."))
      .finally(() => setDrawerLoading(false));
  }, [threadParam, threads]);

  const openHitlDrawer = (item: HitlItem) => {
    if (!canOpenHitlDrawer) {
      openUpgradeModal("Open HITL drawer");
      return;
    }
    applySearch({ openId: item.id, threadId: item.thread_id ?? threadParam ?? null });
  };

  const closeHitlDrawer = () => {
    applySearch({ openId: null });
  };

  const renderThreadsPanel = () => (
    <section className="aa-glass-card rounded-2xl p-6 space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="aa-page-title text-lg font-semibold">Threads Overview</h2>
          <p className="text-sm text-muted-foreground">Live ReAct reasoning threads per agent.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <select
            value={agentFilter}
            onChange={(event) => setAgentFilter(event.target.value)}
            className="rounded bg-muted border border-border px-2 py-1"
          >
            <option value="all">All sources</option>
            {agentsList.map((agent) => (
              <option key={agent} value={agent}>
                {agent}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-foreground/70">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-border bg-muted"
              checked={activeOnly}
              onChange={(event) => setActiveOnly(event.target.checked)}
            />
            Active only
          </label>
        </div>
      </header>
      {threadsLoading ? (
        <p className="text-sm text-muted-foreground">Loading threads…</p>
      ) : filteredThreads.length === 0 ? (
        <p className="text-sm text-muted-foreground">No threads match the selected filters.</p>
      ) : (
        <>
          <div className="space-y-3 md:hidden">
            {filteredThreads.map((thread) => (
              <div key={thread.id} className="rounded-2xl border border-border bg-background/40 p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-foreground">{thread.agent}</p>
                  {thread.active ? (
                    <span className="px-2 py-1 text-xs rounded-full bg-emerald-500/20 text-emerald-200">Active</span>
                  ) : (
                    <span className="px-2 py-1 text-xs rounded-full bg-gray-600/20 text-foreground/70">
                      {thread.reason_for_stop || "Stopped"}
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground font-mono" title={thread.id}>
                  {thread.id.slice(0, 8)}…
                </p>
                <p className="text-xs text-muted-foreground">Started: {formatRelativeTime(thread.start_time)}</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-full text-xs"
                  onClick={() => openDrawer(thread)}
                >
                  View thread
                </Button>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto border border-border rounded-xl">
            <table className="min-w-full text-sm">
              <thead className="bg-muted text-muted-foreground uppercase text-xs tracking-wide">
                <tr>
                  <th className="px-4 py-2 text-left">Source</th>
                  <th className="px-4 py-2 text-left">Thread</th>
                  <th className="px-4 py-2 text-left">Started</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {filteredThreads.map((thread) => (
                  <tr key={thread.id} className="hover:bg-muted/60">
                    <td className="px-4 py-3 font-medium text-foreground">{thread.agent}</td>
                    <td className="px-4 py-3 font-mono text-foreground/70" title={thread.id}>
                      {thread.id.slice(0, 8)}…
                    </td>
                    <td className="px-4 py-3 text-foreground/70">{formatRelativeTime(thread.start_time)}</td>
                    <td className="px-4 py-3">
                      {thread.active ? (
                        <span className="px-2 py-1 text-xs rounded-full bg-emerald-500/20 text-emerald-200">Active</span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded-full bg-gray-600/20 text-foreground/70">
                          {thread.reason_for_stop || "Stopped"}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-full text-xs"
                        onClick={() => openDrawer(thread)}
                      >
                        View thread
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );

  const renderBlackboardPanel = () => (
    <section className="aa-glass-card rounded-2xl p-6 space-y-4">
      <header className="flex items-center justify-between">
        <h2 className="aa-page-title text-lg font-semibold">Blackboard Stream</h2>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span>{TIME_RANGES[timeRange].label}</span>
          <Button type="button" variant="ghost" size="sm" className="text-xs" onClick={refreshBlackboard}>
            Refresh
          </Button>
        </div>
      </header>
      {blackboardLoading ? (
        <p className="text-sm text-muted-foreground">Loading notes…</p>
      ) : filteredBlackboardNotes.length === 0 ? (
        <p className="text-sm text-muted-foreground">No recent notes.</p>
      ) : (
        <div className="max-h-96 overflow-y-auto space-y-3">
          {filteredBlackboardNotes.map((note, index) => (
            <div key={`${note.id ?? index}-${note._entry_id ?? index}`} className="border border-border rounded-xl p-4">
              <div className="flex flex-wrap items-center justify-between text-xs text-muted-foreground mb-2">
                <div className="flex gap-3">
                  <span className="px-2 py-0.5 rounded-full bg-muted text-foreground font-semibold">
                    {note.agent ?? "unknown"}
                  </span>
                  <span className="px-2 py-0.5 rounded-full bg-muted text-foreground/70">
                    {note.thread ?? "thread"}
                  </span>
                  <span className="px-2 py-0.5 rounded-full bg-muted text-blue-200 capitalize">
                    {note.status ?? "note"}
                  </span>
                </div>
                <span title={formatDate((note.created_at as string) ?? "")}>
                  {formatRelativeTime((note.created_at as string) ?? "")}
                </span>
              </div>
              <p className="text-sm text-foreground/80 whitespace-pre-wrap break-words">
                {summarizeNote(note) || "—"}
              </p>
            </div>
          ))}
        </div>
      )}
    </section>
  );

  const renderTimelinePanel = () => (
    <section className="space-y-4">
      <div className="aa-glass-card rounded-2xl p-4">
        <h3 className="aa-page-title mb-3 text-sm font-semibold">Activity {TIME_RANGES[timeRange].label.toLowerCase()}</h3>
        <div className="flex flex-wrap gap-4 text-sm">
          <ActivityBadge label="Planning" value={activitySummary.thoughts} color="text-amber-200" />
          <ActivityBadge label="Work started" value={activitySummary.actions} color="text-sky-200" />
          <ActivityBadge label="HITL" value={activitySummary.hitl} color="text-rose-200" />
          <ActivityBadge label="Completed" value={activitySummary.executed} color="text-emerald-200" />
          <ActivityBadge label="Hand-offs" value={activitySummary.delegations} color="text-indigo-200" />
          <ActivityBadge label="Issues" value={activitySummary.errors} color="text-rose-300" />
        </div>
      </div>
      <section className="aa-glass-card rounded-2xl p-6 space-y-4">
        <header className="flex items-center justify-between">
          <div>
            <h2 className="aa-page-title text-lg font-semibold">Global timeline</h2>
            <p className="text-sm text-muted-foreground">Recent events across all agents.</p>
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span>{TIME_RANGES[timeRange].label}</span>
            <Button type="button" variant="ghost" size="sm" className="text-xs" onClick={refreshActivity}>
              Refresh
            </Button>
          </div>
        </header>
        {filteredActivityEvents.length === 0 ? (
          <p className="text-sm text-muted-foreground">No recent events.</p>
        ) : (
          <div className="space-y-3">
            {filteredActivityEvents.slice(0, timelineVisible).map((event, index) => (
              <article key={`${event.id ?? index}-${event.event_type}`} className="aa-glass-card rounded-xl p-4">
                <div className="flex flex-wrap items-center justify-between text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">{event.agent ?? "agent"}</span>
                  <span>{formatDate(event.timestamp)}</span>
                </div>
                <p className="text-sm text-foreground/80 mt-2">
                  {event.event_type ?? "event"} · {event.summary ?? (event as any).thread_id ?? ""}
                </p>
                {(event as any).payload && (
                  <pre className="mt-2 text-xs text-muted-foreground bg-background rounded p-3 overflow-x-auto">
                    {JSON.stringify((event as any).payload, null, 2)}
                  </pre>
                )}
              </article>
            ))}
            {filteredActivityEvents.length > timelineVisible && (
              <div className="flex items-center justify-between pt-2 text-xs text-muted-foreground">
                <span>Showing {timelineVisible} of {filteredActivityEvents.length} events</span>
                <Button type="button" variant="ghost" size="sm" className="text-xs" onClick={() => setTimelineVisible((prev) => prev + 25)}>
                  Show 25 more
                </Button>
              </div>
            )}
          </div>
        )}
      </section>
    </section>
  );

  const renderInboxPanel = () => (
    <CapabilityGate
      flag="canViewHitl"
      fallback={
        <LockedFeatureCard
          title="Upgrade for HITL observability"
          description="View, assign, and inspect approval queues with Pro-tier."
          featureId="canViewHitl"
        />
      }
    >
      <section className="aa-glass-card rounded-2xl p-6 space-y-4">
        <header className="flex items-center justify-between">
          <div>
            <h2 className="aa-page-title text-lg font-semibold">HITL Queue</h2>
            <p className="text-sm text-muted-foreground">Pending approvals awaiting human review.</p>
          </div>
          <div className="flex items-center gap-3">
            <Button type="button" variant="ghost" size="sm" className="text-xs" onClick={refreshHitl}>
              Refresh
            </Button>
          </div>
        </header>
        {hitlLoading ? (
          <p className="text-sm text-muted-foreground">Loading HITL queue…</p>
        ) : hitlQueue.length === 0 ? (
          <p className="text-sm text-muted-foreground">No pending approvals.</p>
        ) : (
          <div className="space-y-3">
            {hitlQueue.slice(0, hitlVisible).map((item) => (
              <div key={item.id} className="aa-glass-card rounded-xl p-4 space-y-3">
                  <div className="flex flex-wrap items-center justify-between text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground">{item.category ?? "general"}</span>
                    <span>{formatDate(item.created_at ?? undefined)}</span>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-foreground">{item.title ?? item.node_key ?? "HITL item"}</p>
                      <p className="text-xs text-muted-foreground">{item.summary ?? item.description ?? item.agent ?? "Waiting"}</p>
                    </div>
                    <HitlBadge status={item.status} deadline={item.deadline} />
                  </div>
                  <pre className="text-xs text-muted-foreground bg-background rounded p-3 overflow-x-auto">
                    {JSON.stringify(item.payload ?? {}, null, 2)}
                  </pre>
                  <div className="flex flex-wrap items-center justify-between gap-3 text-xs">
                    <CapabilityGate
                      flag="canResolveHitlItem"
                      fallback={
                        <LockedFeatureCard
                          title="Unlock HITL actions"
                          description="Upgrade to open drawers, follow workflows, and assign owners."
                          featureId="canResolveHitlItem"
                        />
                      }
                    >
                      <div className="flex flex-wrap gap-2">
                        <CapabilityGate
                          flag="canOpenHitlDrawer"
                          fallback={
                            <LockedFeatureCard
                              title="Open HITL drawer"
                              description="Upgrade to inspect workflow payloads."
                              featureId="canOpenHitlDrawer"
                            />
                          }
                        >
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="rounded-full text-xs"
                            onClick={() => openHitlDrawer(item)}
                          >
                            Open drawer
                          </Button>
                        </CapabilityGate>
                      </div>
                      <CapabilityGate
                        flag="canAssignHitl"
                        fallback={
                          <LockedFeatureCard
                            className="mt-2"
                            title="Assign reviewers"
                            description="Upgrade to re-route HITL items."
                            featureId="canAssignHitl"
                          />
                        }
                      >
                        <HitlAssignMenu
                          assignedLabel={item.assigned_to_display ?? item.assigned_to}
                          onAssign={async (value) => {
                            await hitlActions.assignTo(value, item.id);
                            refreshHitl();
                          }}
                        />
                      </CapabilityGate>
                    </CapabilityGate>
                  </div>
                </div>
            ))}
            {hitlQueue.length > hitlVisible && (
              <div className="flex items-center justify-between pt-2 text-xs text-muted-foreground">
                <span>Showing {hitlVisible} of {hitlQueue.length} approvals</span>
                <Button type="button" variant="ghost" size="sm" className="text-xs" onClick={() => setHitlVisible((prev) => prev + 10)}>
                  Show 10 more
                </Button>
              </div>
            )}
          </div>
        )}
      </section>
    </CapabilityGate>
  );

  const insightTabs = useMemo(() => {
    const tabs = [
      { id: "threads", label: "Threads" },
      { id: "blackboard", label: "Blackboard" },
      { id: "timeline", label: "Timeline" },
    ];
    if (canViewHitl) tabs.push({ id: "inbox", label: "HITL" });
    return tabs;
  }, [canViewHitl]);
  const [activeTab, setActiveTab] = useState<string>("threads");

  useEffect(() => {
    if (!insightTabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(insightTabs[0]?.id ?? "threads");
    }
  }, [activeTab, insightTabs]);

  const renderActiveTab = () => {
    switch (activeTab) {
      case "threads":
        return renderThreadsPanel();
      case "blackboard":
        return renderBlackboardPanel();
      case "timeline":
        return renderTimelinePanel();
      case "inbox":
        return renderInboxPanel();
      default:
        return renderThreadsPanel();
    }
  };

  return (
    <div className="aa-page-shell space-y-8">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div className="space-y-2">
          <p className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/40 px-3 py-1 text-xs uppercase tracking-[0.2em] text-foreground/70">
            <Sparkles className="h-3.5 w-3.5" /> Band Dashboard
          </p>
          <h1 className="aa-page-title text-3xl font-semibold">
            <span className="aa-gradient-text">Band Activity Dashboard</span>
          </h1>
          <p className="text-sm text-muted-foreground">
            Upcoming shows, tasks, approvals, and the latest updates in one place.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            variant="outline"
            className="rounded-full"
            onClick={refreshAll}
          >
            <RefreshCw className={`h-4 w-4 ${threadsLoading || blackboardLoading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <span className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm ${
            sseConnected
              ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
              : "border-border text-muted-foreground"
          }`}>
            <span className={`h-2 w-2 rounded-full ${sseConnected ? "bg-emerald-500 animate-pulse" : "bg-muted-foreground/50"}`} />
            {sseConnected ? "Live" : "Connecting…"}
          </span>
        </div>
      </header>

      {alertItems.length > 0 && (
        <section className="rounded-3xl border border-amber-400/50 bg-amber-500/10 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-amber-200">
              <AlertTriangle className="h-4 w-4" />
              Attention needed
            </div>
            <div className="flex flex-wrap gap-2">
              {alertItems.map((alert, index) => (
                <div key={`${alert.label}-${index}`} className="rounded-2xl border border-amber-400/40 bg-background/40 px-3 py-2 text-xs text-foreground/80">
                  <p className="font-semibold text-foreground">{alert.label}</p>
                  <p className="text-muted-foreground">{alert.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-border/60 bg-background/40 px-4 py-3">
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span className="uppercase tracking-[0.2em] text-[10px] text-foreground/70">Filters</span>
          <select
            value={timeRange}
            onChange={(event) => { setTimeRange(event.target.value as TimeRangeKey); setTimelineVisible(25); }}
            className="rounded-full border border-border bg-background px-3 py-1 text-xs text-foreground/80"
          >
            {Object.entries(TIME_RANGES).map(([key, value]) => (
              <option key={key} value={key}>
                {value.label}
              </option>
            ))}
          </select>
          <select
            value={eventFilter}
            onChange={(event) => { setEventFilter(event.target.value as EventFilter); setTimelineVisible(25); }}
            className="rounded-full border border-border bg-background px-3 py-1 text-xs text-foreground/80"
          >
            <option value="all">All events</option>
            <option value="actions">Work started</option>
            <option value="thoughts">Planning</option>
            <option value="hitl">HITL</option>
            <option value="errors">Issues</option>
            <option value="executed">Completed</option>
            <option value="delegations">Hand-offs</option>
          </select>
        </div>
        <div className="text-xs text-muted-foreground">
          Showing {filteredActivityEvents.length} events
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-[1.2fr_1fr_1fr]">
        <div className="relative overflow-hidden rounded-3xl border border-border/70 bg-gradient-to-br from-amber-500/10 via-background to-sky-500/10 p-6">
          <div className="absolute -right-24 -top-24 h-48 w-48 rounded-full bg-amber-400/20 blur-3xl" />
          <div className="absolute -left-16 bottom-0 h-40 w-40 rounded-full bg-sky-400/20 blur-3xl" />
          <div className="relative z-10 space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-background/80">
                <Signal className="h-5 w-5 text-amber-300" />
              </div>
              <div>
                <p className="text-sm text-foreground/70">What’s happening now</p>
                <p className="text-2xl font-semibold text-foreground">{actionQueue.length} items need attention</p>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <PulseTile icon={Users} label="Band pulse signals" value={formatNumber(Math.max(artistSignals.length, 0))} />
              <PulseTile icon={ListTodo} label="Pending approvals" value={formatNumber(metrics.hitlPending)} />
              <PulseTile icon={MessageSquare} label="Recent updates" value={formatNumber(conversationItems.length)} />
              <PulseTile icon={Timer} label="Last refresh" value={lastRefresh ? formatRelativeTime(lastRefresh.toISOString()) : "—"} />
            </div>
          </div>
        </div>
        <div className="aa-glass-card rounded-3xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-background/60">
                <Activity className="h-4 w-4 text-emerald-300" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Recent activity</p>
                <p className="text-lg font-semibold text-foreground">{filteredActivityEvents.length} updates</p>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <MiniBar label="Planning" value={activitySummary.thoughts} tone="bg-amber-400" />
            <MiniBar label="Work started" value={activitySummary.actions} tone="bg-sky-400" />
            <MiniBar label="Completed" value={activitySummary.executed} tone="bg-emerald-400" />
            <MiniBar label="Hand-offs" value={activitySummary.delegations} tone="bg-indigo-400" />
            <MiniBar label="Issues" value={activitySummary.errors} tone="bg-rose-400" />
          </div>
        </div>
        <div className="aa-glass-card rounded-3xl p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-background/60">
              <ShieldCheck className="h-4 w-4 text-sky-200" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Your focus</p>
              <p className="text-lg font-semibold text-foreground">{actionQueue.filter((item) => item.severity === "high").length} high-priority items</p>
            </div>
          </div>
          <div className="space-y-3">
            {actionQueue.slice(0, 4).map((item) => {
              const percent = Math.min(100, Math.round(((item.ageMinutes ?? 0) / Math.max(30, staleApprovalMinutes)) * 100));
              return (
                <div key={item.id} className="space-y-2">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground">{item.label}</span>
                    <span>{item.ageMinutes ? `${item.ageMinutes}m old` : "New"}</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-muted">
                    <div className="h-2 rounded-full bg-gradient-to-r from-emerald-400 via-sky-400 to-amber-400" style={{ width: `${percent}%` }} />
                  </div>
                </div>
              );
            })}
            {actionQueue.length === 0 && (
              <p className="text-sm text-muted-foreground">No urgent items right now.</p>
            )}
          </div>
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.6fr_1fr]">
        <div className="space-y-6">
          <section className="aa-glass-card rounded-3xl p-6 space-y-4">
            <header className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="aa-page-title text-xl font-semibold">What needs your attention</h2>
                <p className="text-sm text-muted-foreground">Approvals and tasks that need action.</p>
              </div>
              <span className="text-xs text-muted-foreground">{actionQueue.length} items</span>
            </header>
            {actionQueue.length === 0 ? (
              <p className="text-sm text-muted-foreground">No urgent actions right now.</p>
            ) : (
              <div className="space-y-3">
                {actionQueue.slice(0, 6).map((item) => (
                  <div key={item.id} className="rounded-2xl border border-border/60 bg-background/40 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                      <span className="font-semibold text-foreground">{item.label}</span>
                      <span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wide ${severityTone(item.severity)}`}>
                        {item.severity}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-foreground/80">{item.description}</p>
                    <div className="mt-3 flex flex-wrap items-center justify-between text-xs text-muted-foreground">
                      <span>{item.ageMinutes !== undefined ? `${item.ageMinutes}m old` : "New"}</span>
                      {item.actionType === "hitl" ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-xs"
                          onClick={() => {
                            const target = hitlQueue.find((hitl) => hitl.id === item.id);
                            if (target) openHitlDrawer(target);
                          }}
                        >
                          Review
                        </Button>
                      ) : item.agent ? (
                        <Link
                          to={`/agents/${item.agent}`}
                          className="text-xs font-semibold text-primary hover:text-foreground"
                        >
                          Go to agent →
                        </Link>
                      ) : (
                        <span className="text-xs text-muted-foreground">Investigate agent</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="aa-glass-card rounded-3xl p-6 space-y-4">
            <header className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="aa-page-title text-xl font-semibold">Upcoming shows & events</h2>
                <p className="text-sm text-muted-foreground">What’s on the calendar for the band.</p>
              </div>
              <div className="flex items-center gap-3">
                <Link to="/calendar" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:text-foreground">
                  View calendar
                  <ChevronRight className="h-3 w-3" />
                </Link>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-xs"
                  onClick={refreshUpcoming}
                >
                  Refresh
                </Button>
              </div>
            </header>
            {upcomingLoading ? (
              <p className="text-sm text-muted-foreground">Loading events…</p>
            ) : upcomingEvents.length === 0 ? (
              <p className="text-sm text-muted-foreground">No upcoming shows yet.</p>
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                {upcomingEvents.map((event) => (
                  <Link
                    key={event.id}
                    to="/calendar"
                    className="block rounded-2xl border border-border/60 bg-background/40 p-4 space-y-2 transition-colors hover:border-primary/40 hover:bg-background/60"
                  >
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span className="font-semibold text-foreground">{formatShortDate(event.date)}</span>
                      <span className="uppercase tracking-wide text-[10px]">{event.source}</span>
                    </div>
                    <p className="text-sm font-semibold text-foreground">{event.title}</p>
                    {event.location && <p className="text-xs text-muted-foreground">{event.location}</p>}
                  </Link>
                ))}
              </div>
            )}
          </section>

          <section className="aa-glass-card rounded-3xl p-6 space-y-4">
            <header className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="aa-page-title text-xl font-semibold">Band pulse</h2>
                <p className="text-sm text-muted-foreground">Signals distilled from recent activity.</p>
              </div>
              <Link to="/artists" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:text-foreground">
                View artists
                <ChevronRight className="h-3 w-3" />
              </Link>
            </header>
            {artistSignals.length === 0 ? (
              <p className="text-sm text-muted-foreground">No artist signals detected yet.</p>
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                {artistSignals.map((signal) => (
                  <Link key={signal.name} to="/artists" className="block rounded-2xl border border-border/60 bg-background/40 p-4 space-y-3 transition-colors hover:border-primary/40 hover:bg-background/60">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-foreground">{signal.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {signal.agents.length ? `Sources: ${signal.agents.join(", ")}` : "No source tags"}
                        </p>
                      </div>
                      <span className={`rounded-full border px-3 py-1 text-xs uppercase tracking-wide ${getMomentumTone(signal.momentum)}`}>
                        {signal.momentum}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{signal.signals} signals</span>
                      <span>{signal.last_seen ? formatRelativeTime(signal.last_seen) : "—"}</span>
                    </div>
                    {signal.highlight && (
                      <p className="text-xs text-foreground/80 line-clamp-2">{signal.highlight}</p>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </section>

          <section className="aa-glass-card rounded-3xl p-6 space-y-4">
            <header className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="aa-page-title text-xl font-semibold">Recent updates</h2>
                <p className="text-sm text-muted-foreground">Summaries from what the team and agents just worked on.</p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-xs"
                onClick={refreshBlackboard}
              >
                Refresh
                <ChevronRight className="h-4 w-4" />
              </Button>
            </header>
            {blackboardLoading ? (
              <p className="text-sm text-muted-foreground">Loading conversations…</p>
            ) : conversationItems.length === 0 ? (
              <p className="text-sm text-muted-foreground">No conversations yet.</p>
            ) : (
              <div className="space-y-3">
                {conversationItems.map((item, index) => (
                  <div key={`${item.agent}-${index}`} className="rounded-2xl border border-border/60 bg-background/40 p-4">
                    <div className="flex flex-wrap items-center justify-between text-xs text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-muted">
                          <Bot className="h-4 w-4 text-foreground/70" />
                        </span>
                        <span className="font-semibold text-foreground">{item.agent}</span>
                        <span className="rounded-full border border-border/70 px-2 py-0.5 text-[10px] uppercase tracking-wide">
                          {item.status}
                        </span>
                      </div>
                      <span>{formatRelativeTime(item.createdAt)}</span>
                    </div>
                    <p className="mt-2 text-sm text-foreground/80 line-clamp-3">{item.summary}</p>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <div className="space-y-6">
          <section className="aa-glass-card rounded-3xl p-6 space-y-4">
            <header className="flex items-center justify-between">
              <div>
                <h2 className="aa-page-title text-lg font-semibold">My queue</h2>
                <p className="text-sm text-muted-foreground">Approvals assigned to you.</p>
              </div>
            </header>
            {hitlLoading ? (
              <p className="text-sm text-muted-foreground">Loading approvals…</p>
            ) : hitlQueue.filter((item) => item.assigned_to_me).length === 0 ? (
              <p className="text-sm text-muted-foreground">Nothing assigned to you.</p>
            ) : (
              <div className="space-y-3">
                {hitlQueue
                  .filter((item) => item.assigned_to_me)
                  .slice(0, 5)
                  .map((item) => (
                    <div key={item.id} className="rounded-2xl border border-border/60 bg-background/40 p-3 space-y-2">
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span className="font-semibold text-foreground">{item.category ?? "general"}</span>
                        <HitlBadge status={item.status} deadline={item.deadline} />
                      </div>
                      <p className="text-sm font-semibold text-foreground">{item.title ?? item.node_key ?? "Approval"}</p>
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {item.summary ?? item.description ?? item.agent ?? "Awaiting review"}
                      </p>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>{formatRelativeTime(item.created_at ?? undefined)}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-xs"
                          onClick={() => openHitlDrawer(item)}
                        >
                          Review
                        </Button>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </section>

          <section className="aa-glass-card rounded-3xl p-6 space-y-4">
            <header className="flex items-center justify-between">
              <div>
                <h2 className="aa-page-title text-lg font-semibold">Approvals</h2>
                <p className="text-sm text-muted-foreground">Pending approvals and tasks waiting on you.</p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-xs"
                onClick={refreshHitl}
              >
                Refresh
                <ChevronRight className="h-4 w-4" />
              </Button>
            </header>
            {hitlLoading ? (
              <p className="text-sm text-muted-foreground">Loading approvals…</p>
            ) : hitlQueue.length === 0 ? (
              <p className="text-sm text-muted-foreground">No approvals waiting.</p>
            ) : (
              <div className="space-y-3">
                {hitlQueue.slice(0, 4).map((item) => (
                  <div key={item.id} className="rounded-2xl border border-border/60 bg-background/40 p-3 space-y-2">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span className="font-semibold text-foreground">{item.category ?? "general"}</span>
                      <HitlBadge status={item.status} deadline={item.deadline} />
                    </div>
                    <p className="text-sm font-semibold text-foreground">{item.title ?? item.node_key ?? "Approval"}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {item.summary ?? item.description ?? item.agent ?? "Awaiting review"}
                    </p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{formatRelativeTime(item.created_at ?? undefined)}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-xs"
                        onClick={() => openHitlDrawer(item)}
                      >
                        Review
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="aa-glass-card rounded-3xl p-6 space-y-4">
            <header className="flex items-center justify-between">
              <div>
                <h2 className="aa-page-title text-lg font-semibold">Behind the scenes</h2>
                <p className="text-sm text-muted-foreground">Recent work threads you can drill into.</p>
              </div>
            </header>
            {threadsLoading ? (
              <p className="text-sm text-muted-foreground">Loading roster…</p>
            ) : filteredThreads.length === 0 ? (
              <p className="text-sm text-muted-foreground">No active agents right now.</p>
            ) : (
              <div className="space-y-3">
                {filteredThreads.slice(0, 5).map((thread) => (
                  <Button
                    key={thread.id}
                    type="button"
                    variant="ghost"
                    onClick={() => openDrawer(thread)}
                    className="w-full rounded-2xl border border-border/60 bg-background/40 p-3 text-left transition hover:border-border hover:bg-background/60"
                  >
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span className="font-semibold text-foreground">{thread.agent}</span>
                      <span>{thread.active ? "Live" : "Idle"}</span>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                      <span>{thread.turns} updates</span>
                      <span>{formatRelativeTime(thread.start_time)}</span>
                    </div>
                  </Button>
                ))}
              </div>
            )}
          </section>
 
          <section className="aa-glass-card rounded-3xl p-6 space-y-4">
            <header className="flex items-center justify-between">
              <div>
                <h2 className="aa-page-title text-lg font-semibold">Thresholds</h2>
                <p className="text-sm text-muted-foreground">Tune when alerts and queue severity kick in.</p>
              </div>
            </header>
            <div className="space-y-4 text-sm text-muted-foreground">
              <label className="flex flex-col gap-2">
                <span>Pending approvals alert</span>
                <input
                  type="number"
                  min={1}
                  value={pendingThreshold}
                  onChange={(event) => setPendingThreshold(Number(event.target.value) || 1)}
                  className="rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground"
                />
              </label>
              <label className="flex flex-col gap-2">
                <span>Stale approval minutes</span>
                <input
                  type="number"
                  min={15}
                  value={staleApprovalMinutes}
                  onChange={(event) => setStaleApprovalMinutes(Number(event.target.value) || 60)}
                  className="rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground"
                />
              </label>
              <label className="flex flex-col gap-2">
                <span>No activity alert (minutes)</span>
                <input
                  type="number"
                  min={5}
                  value={quietMinutesThreshold}
                  onChange={(event) => setQuietMinutesThreshold(Number(event.target.value) || 15)}
                  className="rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground"
                />
              </label>
            </div>
          </section>

          <section className="aa-glass-card rounded-3xl p-6 space-y-4">
            <header className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-background/60">
                <Users className="h-4 w-4 text-amber-300" />
              </div>
              <div>
                <h2 className="aa-page-title text-lg font-semibold">Plan readiness</h2>
                <p className="text-sm text-muted-foreground">MCP tools + HITL guardrails engaged.</p>
              </div>
            </header>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center justify-between rounded-xl border border-border/60 bg-background/40 px-3 py-2">
                <span>MCP tasks available</span>
                <span className="text-emerald-200">Enabled</span>
              </li>
              <li className="flex items-center justify-between rounded-xl border border-border/60 bg-background/40 px-3 py-2">
                <span>HITL approvals</span>
                <span className="text-emerald-200">Active</span>
              </li>
              <li className="flex items-center justify-between rounded-xl border border-border/60 bg-background/40 px-3 py-2">
                <span>Streaming timeline</span>
                <span className="text-emerald-200">Live</span>
              </li>
              <li className="flex items-center justify-between rounded-xl border border-border/60 bg-background/40 px-3 py-2">
                <span>Autopilot rules</span>
                <span className="text-emerald-200">Ready</span>
              </li>
            </ul>
          </section>
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="aa-page-title text-xl font-semibold">Deep dive</h2>
            <p className="text-sm text-muted-foreground">Explore raw thread, blackboard, and timeline data.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {insightTabs.map((tab) => (
              <Button
                key={tab.id}
                type="button"
                variant={tab.id === activeTab ? "secondary" : "outline"}
                size="sm"
                className="rounded-full px-4 text-sm"
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </Button>
            ))}
          </div>
        </div>
        {renderActiveTab()}
      </section>

      {drawer && (
        <div className="fixed inset-0 z-40 flex">
          <div className="flex-1 bg-black/60" onClick={closeDrawer} />
          <div className="relative w-full max-w-3xl bg-background border-l border-border h-full overflow-y-auto p-6 space-y-6">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <h3 className="aa-page-title text-xl font-semibold">{drawer.thread.agent}</h3>
                <p className="font-mono text-sm text-muted-foreground" title={drawer.thread.id}>
                  {drawer.thread.id.slice(0, 8)}…
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Link
                  to={`/agents/${drawer.thread.agent.toLowerCase().replace(/agent$/i, "")}?thread=${drawer.thread.id}`}
                  onClick={closeDrawer}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs text-foreground/80 transition-colors hover:bg-muted hover:text-foreground"
                >
                  <MessageSquare className="h-3 w-3" />
                  Open in chat
                </Link>
                <Button type="button" variant="ghost" size="sm" className="text-sm" onClick={closeDrawer}>
                  Close
                </Button>
              </div>
            </div>
            {drawerLoading ? (
              <div className="space-y-3">
                <div className="h-4 w-1/3 animate-pulse rounded bg-muted" />
                <div className="h-12 w-full animate-pulse rounded bg-muted" />
                <div className="h-12 w-full animate-pulse rounded bg-muted" />
                <div className="h-12 w-5/6 animate-pulse rounded bg-muted" />
              </div>
            ) : drawerError ? (
              <p className="text-sm text-destructive">{drawerError}</p>
            ) : drawer.notes.length === 0 ? (
              <p className="text-sm text-muted-foreground">No notes recorded for this thread.</p>
            ) : (
              <ul className="space-y-4">
                {drawer.notes.map((note, index) => (
                  <li key={`${note.id ?? index}-${note._entry_id ?? index}`} className="relative pl-6">
                    <span className="absolute left-0 top-1 h-3 w-3 rounded-full bg-primary" />
                    <div className="text-xs text-muted-foreground" title={formatDate((note.created_at as string) ?? "")}>
                      {formatRelativeTime((note.created_at as string) ?? "")}
                    </div>
                    <div className="text-sm font-semibold text-foreground capitalize">{note.status ?? "note"}</div>
                    <p className="text-sm text-foreground/80 whitespace-pre-wrap break-words">
                      {summarizeNote(note) || "—"}
                    </p>
                    {(note.payload || note.observation) && (
                      <pre className="mt-2 text-xs text-muted-foreground bg-background/40 rounded p-3 overflow-x-auto">
                        {JSON.stringify(note.payload ?? note.observation ?? {}, null, 2)}
                      </pre>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <AgentInboxPanel agentName={drawer.thread.agent} />
          </div>
        </div>
      )}

      <CapabilityGate
        flag="canOpenHitlDrawer"
        fallback={selectedHitlId ? <LockedHitlDrawer onClose={closeHitlDrawer} feature="canOpenHitlDrawer" /> : null}
      >
        <HitlDetailDrawer
          open={Boolean(selectedHitlId)}
          hitlId={selectedHitlId}
          item={selectedHitlId ? hitlQueue.find((entry) => entry.id === selectedHitlId) ?? null : null}
          onClose={closeHitlDrawer}
          onActionComplete={() => refreshHitl()}
        />
      </CapabilityGate>
    </div>
  );
}

type PulseTileProps = {
  icon: typeof Sparkles;
  label: string;
  value: string | number;
};

function PulseTile({ icon: Icon, label, value }: PulseTileProps) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-border/60 bg-background/40 p-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-background/70">
        <Icon className="h-4 w-4 text-foreground/70" />
      </div>
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-lg font-semibold text-foreground">{value}</p>
      </div>
    </div>
  );
}

type ActivityBadgeProps = {
  label: string;
  value: number;
  color: string;
};

function ActivityBadge({ label, value, color }: ActivityBadgeProps) {
  return (
    <div className="aa-glass-card flex items-center gap-2 rounded-xl px-3 py-2">
      <span className={`text-xs uppercase tracking-wide ${color}`}>{label}</span>
      <span className="text-lg font-semibold text-foreground">{value}</span>
    </div>
  );
}

type MiniBarProps = {
  label: string;
  value: number;
  tone: string;
};

function MiniBar({ label, value, tone }: MiniBarProps) {
  const width = Math.min(100, value * 12);
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{label}</span>
        <span>{value}</span>
      </div>
      <div className="h-2 w-full rounded-full bg-muted">
        <div className={`h-2 rounded-full ${tone}`} style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}
